/**
 * Event cards and the race-history row.
 *
 * The whole card is the target — there are no inline RSVP buttons, because
 * volunteering is driven from inside the event, not from a list. SPEC.md §6.2.
 */

import { dateBlock, eventLinkLabel, KIND_LABEL } from '../data';
import { Pill } from './ui';

/**
 * Which upcoming event is TRENDING — the one the club is actually turning up
 * for. One winner, and only when the interest is real (three RSVPs at club
 * scale): a "trending" tag on a meeting nobody has answered would be noise
 * wearing a flame.
 */
export function trendingEventId(events, todayISO) {
  let best = null;
  for (const e of events) {
    if (e.date < todayISO) continue;
    if ((e.rsvp?.length ?? 0) < 3) continue;
    if (!best || e.rsvp.length > best.rsvp.length) best = e;
  }
  return best?.id ?? null;
}

/** The crimson flame tag. Shared by the card and the detail hero. */
export function TrendingTag() {
  return (
    <span className="inline-flex items-center gap-1 self-center whitespace-nowrap rounded-full bg-flag-500 px-2 py-0.5 font-market text-[9.5px] font-semibold uppercase tracking-wide text-white">
      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.5 0.7c0.3 4.3-1.4 6.6-3.4 8.6-1.9 1.9-3.6 4-3.6 7.2 0 4.1 3.4 7.5 7.5 7.5s7.5-3.4 7.5-7.5c0-3.2-1.6-5.9-3.3-8.1-0.9 1.6-1.9 2.6-3.1 3.3 0.3-3.8-0.5-8.1-3.6-11z" />
      </svg>
      Trending
    </span>
  );
}

// `trending` — pass true for the event trendingEventId() picked; the card
// gets the flame tag and, when it has an image, the poster treatment.
export function EventCard({ event, db, meId, onOpen, trending = false }) {
  const { dd, mm } = dateBlock(event.date);
  const going = event.rsvp.length;
  const iAmIn = !!meId && event.rsvp.includes(meId);

  const depts = db.departments.filter((d) => d.eventId === event.id);
  const slots = depts.reduce((sum, d) => sum + d.needed, 0);
  const filled = depts.reduce(
    (sum, d) => sum + db.signups.filter((s) => s.deptId === d.id).length,
    0,
  );

  /* The committee's own web page for this event, when there is one. The pill
     reads "Event page", not the address: a host long enough to be a race
     domain truncates to gibberish at card width, and the full address is on
     the tooltip and the event page for anyone who wants to see it first. */
  const hasLink = Boolean(eventLinkLabel(event.linkUrl));

  /* One card style for every kind — the kind is named in the label, not the
     colour. The banner image deliberately does NOT appear here (owner's
     direction, 17 Aug 2026): a list of full-bleed posters stops being a list.
     The image lives on the event PAGE, where there is room for it.

     The card is a BUTTON under everything rather than around everything: an
     <a> cannot live inside a <button>, and the event page link has to be a
     real anchor so it opens in its own tab. So the button is an overlay, the
     content ignores pointer events, and the link alone takes them back. */
  return (
    <div className="lift group relative mb-3 flex w-full overflow-hidden border border-line bg-white text-left hover:bg-ground-warm">
      <button
        type="button"
        className="absolute inset-0 cursor-pointer"
        onClick={() => onOpen(event.id)}
        aria-label={`${event.title} — open event`}
      />
      {/* The date tile is an ink block, and crimson for a race — the one kind
          where turning up on the wrong day actually costs you an entry fee. */}
      <div
        className={`pointer-events-none relative w-[62px] flex-none px-2 py-3 text-center text-white ${
          event.kind === 'race' ? 'bg-flag-500' : 'bg-ink-800'
        }`}
      >
        <div className="font-market text-[26px] font-extrabold leading-[0.9]">{dd}</div>
        <div className="mt-1 font-market text-[9px] uppercase tracking-[0.12em] opacity-70">{mm}</div>
      </div>
      <div className="pointer-events-none relative min-w-0 flex-1 p-3.5">
        <div className="flex flex-wrap items-center gap-2 font-market text-[8.5px] uppercase tracking-[0.14em] text-copy-label">
          {KIND_LABEL[event.kind]} · {event.time}
          {trending && <TrendingTag />}
        </div>
        <div className="mt-0.5 font-market text-[19px] tracking-[-0.025em] font-bold leading-tight">
          {event.title}
        </div>
        <div className="mt-1 font-market text-[10.5px] leading-normal text-copy-muted">
          {event.venue}
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <Pill>{going} going</Pill>
          {iAmIn && (
            <span className="flex items-center gap-1 font-market text-[10px] text-sea-600">
              ✓ You’re in
            </span>
          )}
          {depts.length > 0 && (
            <Pill tone="ok">
              {filled}/{slots} volunteers
            </Pill>
          )}
          {hasLink && (
            /* The one live target inside the card: everything else opens the
               event, this opens the event's own site in a new tab. */
            <a
              href={event.linkUrl}
              target="_blank"
              rel="noreferrer noopener"
              onClick={(e) => e.stopPropagation()}
              title={event.linkUrl}
              className="pointer-events-auto inline-flex items-center gap-1 whitespace-nowrap border border-line-strong px-1.5 py-0.5 font-market text-[10px] uppercase tracking-[0.06em] text-copy-muted transition-colors duration-200 hover:border-ink-700 hover:text-ink-700"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M14 4h6v6" />
                <path d="M20 4 10 14" />
                <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
              </svg>
              <span>Event page</span>
            </a>
          )}
          <span className="ml-auto font-market text-[12.5px] font-bold uppercase tracking-[0.06em] text-copy-label transition-colors duration-200 group-hover:text-ink-700">
            Open →
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * A past event or completed duty. Deliberately quieter than an upcoming
 * card — history must not compete with work still to be done.
 *
 * `tag` is a rendered node, not a string.
 */
export function HistoryRow({ date, title, meta, tag, onClick }) {
  const { dd, mm } = dateBlock(date);
  const rowClass =
    'mb-2 flex w-full items-center gap-3 border border-line bg-white px-3.5 py-3 text-left opacity-90';
  const body = (
    <>
      <div className="w-[46px] flex-none text-center">
        <div className="font-market text-xl font-extrabold leading-none text-copy-muted">{dd}</div>
        <div className="mt-0.5 font-market text-[8px] uppercase tracking-[0.1em] text-copy-label">
          {mm}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-market text-[15.5px] tracking-[-0.015em] font-bold leading-tight">{title}</div>
        <div className="mt-1 font-market text-[10px] text-copy-muted">{meta}</div>
      </div>
      <div className="flex-none">{tag}</div>
    </>
  );

  if (!onClick) return <div className={rowClass}>{body}</div>;

  return (
    <button type="button" className={`${rowClass} cursor-pointer`} onClick={onClick}>
      {body}
    </button>
  );
}

/** A single race in a member's history, with the PB tag when it is one. */
export function RaceRow({ race, meta, time, isPb }) {
  return (
    <div className="mb-2 flex items-center justify-between gap-3 border border-line bg-white px-3.5 py-3">
      <div>
        <div className="font-market text-base font-semibold uppercase tracking-[0.02em]">
          {race}
        </div>
        <div className="mt-0.5 font-market text-[10.5px] text-copy-muted">{meta}</div>
      </div>
      <div className="whitespace-nowrap text-right font-market text-[15px] font-semibold tabular-nums">
        {time}
        {isPb && (
          <span className="ml-1.5 inline-block rounded bg-gold-500 px-1.5 py-px align-[2px] font-market text-[8px] text-ink-800">
            PB
          </span>
        )}
      </div>
    </div>
  );
}
